import { ActionType, createAction, createAsyncAction } from "typesafe-actions";

import type {
  FiPresetApplyMode,
  FiPresetRoleKey,
  FiWeeklySummaryPreset,
  FiWordCloudPreset,
} from "@mapmycustomers/shared/types/entity/fieldInsight";
import type Organization from "@mapmycustomers/shared/types/Organization";
import type PlatformFilterModel from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";

import type { AnalysisConfig, WordCloudConfig } from "@app/component/executiveSummary/types";
import type CalendarSyncAffectedUser from "@app/types/nylas/CalendarSyncAffectedUser";

export const fetchAffectedUsers = createAsyncAction(
  "settings/organizationDetails/calendarSyncAffectedUsers/fetch/request",
  "settings/organizationDetails/calendarSyncAffectedUsers/fetch/success",
  "settings/organizationDetails/calendarSyncAffectedUsers/fetch/failure"
)<
  {
    callback?: (total: number) => void;
    failureCallback?: () => void;
    filter: PlatformFilterModel;
  },
  { total: number; users: CalendarSyncAffectedUser[] },
  void
>();

export const clearAffectedUsers = createAction(
  "settings/organizationDetails/calendarSyncAffectedUsers/clear"
)();

export const fetchExecutiveSummaryPresets = createAsyncAction(
  "settings/organizationDetails/executiveSummaryPresets/fetch/request",
  "settings/organizationDetails/executiveSummaryPresets/fetch/success",
  "settings/organizationDetails/executiveSummaryPresets/fetch/failure"
)<void, { organizationId: Organization["id"]; presets: FiWeeklySummaryPreset[] }, void>();

// The same read, dispatched to put the cards back in step after a write — never to load the
// section. Most write failures share their cause with reads, so the recovery usually fails too:
// as the ordinary fetch it would then add a second toast and replace the cards with a
// "couldn't load presets" error, after a save. This one reports quietly and leaves them standing.
export const refreshExecutiveSummaryPresets = createAsyncAction(
  "settings/organizationDetails/executiveSummaryPresets/refresh/request",
  "settings/organizationDetails/executiveSummaryPresets/refresh/success",
  "settings/organizationDetails/executiveSummaryPresets/refresh/failure"
)<void, { organizationId: Organization["id"]; presets: FiWeeklySummaryPreset[] }, void>();

export const saveExecutiveSummaryPreset = createAsyncAction(
  "settings/organizationDetails/executiveSummaryPresets/save/request",
  "settings/organizationDetails/executiveSummaryPresets/save/success",
  "settings/organizationDetails/executiveSummaryPresets/save/failure"
)<{ config: AnalysisConfig; roleKey: FiPresetRoleKey }, void, void>();

export const deleteExecutiveSummaryPreset = createAsyncAction(
  "settings/organizationDetails/executiveSummaryPresets/delete/request",
  "settings/organizationDetails/executiveSummaryPresets/delete/success",
  "settings/organizationDetails/executiveSummaryPresets/delete/failure"
)<{ onDone?: () => void; roleKey: FiPresetRoleKey }, void, void>();

export const applyExecutiveSummaryPreset = createAsyncAction(
  "settings/organizationDetails/executiveSummaryPresets/apply/request",
  "settings/organizationDetails/executiveSummaryPresets/apply/success",
  "settings/organizationDetails/executiveSummaryPresets/apply/failure"
)<{ mode: FiPresetApplyMode; onDone?: () => void; roleKey: FiPresetRoleKey }, void, void>();

export const fetchWordCloudPresets = createAsyncAction(
  "settings/organizationDetails/wordCloudPresets/fetch/request",
  "settings/organizationDetails/wordCloudPresets/fetch/success",
  "settings/organizationDetails/wordCloudPresets/fetch/failure"
)<void, { organizationId: Organization["id"]; presets: FiWordCloudPreset[] }, void>();

export const saveWordCloudPreset = createAction(
  "settings/organizationDetails/wordCloudPresets/save"
)<{
  callback: () => void;
  config: WordCloudConfig;
  failureCallback: () => void;
  roleKey: FiPresetRoleKey;
}>();

export const deleteWordCloudPreset = createAsyncAction(
  "settings/organizationDetails/wordCloudPresets/delete/request",
  "settings/organizationDetails/wordCloudPresets/delete/success",
  "settings/organizationDetails/wordCloudPresets/delete/failure"
)<{ onDone: () => void; roleKey: FiPresetRoleKey }, void, void>();

export const applyWordCloudPreset = createAsyncAction(
  "settings/organizationDetails/wordCloudPresets/apply/request",
  "settings/organizationDetails/wordCloudPresets/apply/success",
  "settings/organizationDetails/wordCloudPresets/apply/failure"
)<{ onDone: () => void; roleKey: FiPresetRoleKey }, void, void>();

export type Actions = ActionType<
  | typeof applyExecutiveSummaryPreset
  | typeof applyWordCloudPreset
  | typeof clearAffectedUsers
  | typeof deleteExecutiveSummaryPreset
  | typeof deleteWordCloudPreset
  | typeof fetchAffectedUsers
  | typeof fetchExecutiveSummaryPresets
  | typeof fetchWordCloudPresets
  | typeof refreshExecutiveSummaryPresets
  | typeof saveExecutiveSummaryPreset
  | typeof saveWordCloudPreset
>;
